import { RiSearchLine, RiStore2Line } from "@remixicon/react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { $api } from "@/api/query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

/**
 * Store app: My Store.
 *
 * Only export-marked SKUs with stock in THIS store's branches. This answers
 * "what do I hold that Farfetch is selling", not "what exists in the ERP".
 *
 * Responsive: the wide table collapses to cards under `sm` so shop staff can
 * use it on a phone on the floor.
 */
export const Route = createFileRoute("/_app/store/items")({
	component: StoreItemsPage,
});

interface StoreBranch {
	number: number;
	name: string | null;
	isActive: boolean;
}

interface StoreMe {
	storeId: string;
	storeName: string;
	isActive: boolean;
	branches: StoreBranch[];
	isAdminView: boolean;
}

interface ItemBranch {
	branchNumber: number;
	branchName: string | null;
	stockQuantity: number;
}

interface StoreItem {
	articleSizeId: string;
	identifier: string | null;
	articleNumber: string | null;
	brand: string | null;
	description: string | null;
	size: string;
	/** On hand: the physical shelf count, unaffected by reservations. */
	stockQuantity: number;
	/** Committed to an accepted Farfetch order, no longer sellable. */
	reservedQuantity: number;
	/** stockQuantity - reservedQuantity, clamped at 0. */
	availableQuantity: number;
	branches: ItemBranch[];
}

interface ItemsResponse {
	items: StoreItem[];
	total: number;
	totalUnits: number;
}

function StoreItemsPage() {
	const [search, setSearch] = useState("");
	const [storeId, setStoreId] = useState<string | null>(null);

	// An admin has no store of their own, so /store/me 400s until a store is
	// picked. The picker is fed by the admin-only stores list.
	const me = $api.useQuery(
		"get",
		`/api/v1/store/me${storeId ? `?storeId=${storeId}` : ""}` as never,
	);
	const adminStores = $api.useQuery("get", "/api/v1/stores" as never);

	const itemsQuery = $api.useQuery(
		"get",
		`/api/v1/store/items?${new URLSearchParams({
			...(search ? { search } : {}),
			...(storeId ? { storeId } : {}),
		})}` as never,
	);

	const profile = me.data as StoreMe | undefined;
	const data = itemsQuery.data as ItemsResponse | undefined;
	const storeOptions = (adminStores.data as { id: string; name: string }[] | undefined) ?? [];

	// A store user gets 403 on the admin list; that is expected, not an error to
	// show. Only the store-scoped calls matter for them.
	const needsStorePick = me.isError && storeOptions.length > 0;

	return (
		<div className="space-y-6 p-4 sm:p-6">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<h1 className="flex items-center gap-2 text-xl font-semibold sm:text-2xl">
						<RiStore2Line className="size-5 sm:size-6" aria-hidden />
						{profile?.storeName ?? "My Store"}
					</h1>
					{profile && (
						<div className="mt-2 flex flex-wrap gap-1">
							{profile.branches.map((b) => (
								<Badge key={b.number} variant="secondary" className="text-xs">
									{b.number} {b.name ?? ""}
								</Badge>
							))}
							{profile.branches.length === 0 && (
								<span className="text-sm text-muted-foreground">
									No branches allocated to this store yet.
								</span>
							)}
						</div>
					)}
				</div>

				{(profile?.isAdminView || needsStorePick) && storeOptions.length > 0 && (
					<Select value={storeId ?? undefined} onValueChange={(v) => setStoreId(v)}>
						<SelectTrigger className="w-full sm:w-56" aria-label="Select store">
							<SelectValue placeholder="Select a store" />
						</SelectTrigger>
						<SelectContent>
							{storeOptions.map((s) => (
								<SelectItem key={s.id} value={s.id}>
									{s.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			</div>

			{data && (
				<div className="grid grid-cols-2 gap-3 sm:max-w-sm">
					<Card>
						<CardContent className="pt-6">
							<p className="text-xs uppercase text-muted-foreground">Items in stock</p>
							<p className="text-2xl font-semibold">{data.total}</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<p className="text-xs uppercase text-muted-foreground">Total units</p>
							<p className="text-2xl font-semibold">{data.totalUnits}</p>
						</CardContent>
					</Card>
				</div>
			)}

			<div className="relative max-w-md">
				<RiSearchLine
					className="absolute left-2 top-2.5 size-4 text-muted-foreground"
					aria-hidden
				/>
				<Input
					className="pl-8"
					placeholder="Search brand, article, size"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					aria-label="Search items"
				/>
			</div>

			{itemsQuery.isLoading && (
				<div className="space-y-2">
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
				</div>
			)}

			{itemsQuery.isError && !needsStorePick && (
				<Card>
					<CardContent className="flex flex-col items-start gap-3 pt-6">
						<p role="alert" className="text-sm text-destructive">
							Could not load this store's items.
						</p>
						<Button variant="outline" onClick={() => itemsQuery.refetch()}>
							Retry
						</Button>
					</CardContent>
				</Card>
			)}

			{needsStorePick && (
				<Card>
					<CardContent className="pt-6 text-sm text-muted-foreground">
						Select a store to view its items.
					</CardContent>
				</Card>
			)}

			{data && data.items.length === 0 && (
				<Card>
					<CardContent className="pt-6 text-sm text-muted-foreground">
						No items with stock in this store's branches.
					</CardContent>
				</Card>
			)}

			{data && data.items.length > 0 && (
				<>
					{/* Desktop table */}
					<Card className="hidden sm:block">
						<CardContent className="overflow-x-auto p-0">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Identifier</TableHead>
										<TableHead>Brand</TableHead>
										<TableHead>Article</TableHead>
										<TableHead className="w-20">Size</TableHead>
										<TableHead className="w-20">On hand</TableHead>
										<TableHead className="w-20">Reserved</TableHead>
										<TableHead className="w-20">Available</TableHead>
										<TableHead>Branch</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.items.map((item) => (
										<TableRow key={item.articleSizeId}>
											<TableCell className="font-mono text-xs">
												{item.identifier ?? item.articleNumber ?? "-"}
											</TableCell>
											<TableCell>{item.brand ?? "-"}</TableCell>
											<TableCell className="max-w-xs truncate">{item.description ?? "-"}</TableCell>
											<TableCell>{item.size}</TableCell>
											<TableCell>{item.stockQuantity}</TableCell>
											<TableCell>
												{item.reservedQuantity > 0 ? (
													<Badge variant="secondary" className="text-xs">
														{item.reservedQuantity}
													</Badge>
												) : (
													<span className="text-muted-foreground">-</span>
												)}
											</TableCell>
											{/* Available is the number staff may still promise. */}
											<TableCell className="font-medium">{item.availableQuantity}</TableCell>
											<TableCell className="space-x-1">
												{item.branches.map((b) => (
													<Badge key={b.branchNumber} variant="outline" className="text-xs">
														{b.branchNumber}: {b.stockQuantity}
													</Badge>
												))}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					{/* Mobile cards: shop staff on a phone. */}
					<div className="space-y-2 sm:hidden">
						{data.items.map((item) => (
							<Card key={item.articleSizeId}>
								<CardContent className="space-y-1 pt-4">
									<div className="flex items-start justify-between gap-2">
										<p className="font-medium">{item.brand ?? "-"}</p>
										<Badge variant="secondary">
											{item.size} &middot; {item.availableQuantity}
											{item.reservedQuantity > 0 ? ` of ${item.stockQuantity}` : ""}
										</Badge>
									</div>
									<p className="text-sm text-muted-foreground">{item.description ?? "-"}</p>
									<p className="font-mono text-xs text-muted-foreground">
										{item.identifier ?? item.articleNumber ?? "-"}
									</p>
									<div className="flex flex-wrap gap-1 pt-1">
										{item.branches.map((b) => (
											<Badge key={b.branchNumber} variant="outline" className="text-xs">
												{b.branchNumber}: {b.stockQuantity}
											</Badge>
										))}
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				</>
			)}
		</div>
	);
}
